import React, { useState } from 'react';
import { ControlledModal } from '@instinet-analytics/semantic-ui-components';
import { useDispatchSwitchLayoutAction } from 'layout/store/dispatcher';
import { useMakeRequest } from 'utils/hooks/request';
import { useLayoutSelector } from 'layout/store/selectors';
import { TextInput } from '@instinet-analytics/semantic-ui-components';
import { useInputError } from '@instinet-analytics/core-lib';
import './CreateLayoutModal.scss';
import { createLayoutAction } from 'layoutManagement/store/thunks';
import { isLayoutNameValid } from 'layoutManagement/layoutNameValidator';
import { useLayoutsList } from 'layout/store/useLayoutsList';
import {
    isWellKnownLayoutApiErrorResponse,
    WellKnownLayoutApiErrorCodes,
} from 'layout/WellKnownLayoutApiErrorCodes';
import { showWarningAsync } from 'notification/store/thunks';
import { useDispatch } from 'store/hooks';
import { useAppLayoutOrigin } from 'layout/WellKnownLayoutOrigin';

interface CreateLayoutModalProps {
    isOpen: boolean;
    onActionCompleted: () => void;
}

export default function CreateLayoutModal(props: CreateLayoutModalProps) {
    const { isOpen, onActionCompleted } = props;

    const [layoutName, setLayoutName] = useState('');

    const makeRequest = useMakeRequest();
    const appLayoutOrigin = useAppLayoutOrigin();
    const { forceLayoutListReload } = useLayoutsList(appLayoutOrigin);

    const dispatchSwitchLayout = useDispatchSwitchLayoutAction();
    const dispatch = useDispatch();

    const layout = useLayoutSelector();

    const {
        errorMessage,
        invalidInput,
        setInputTextAndClearInvalidState,
        clearError,
        displayError,
    } = useInputError();

    const createLayout = () => {
        if (!layout) {
            setLayoutName('');
            displayError('Please enter a layout name');
            return;
        }

        const layoutNameValidation = isLayoutNameValid(layoutName);
        if (!layoutNameValidation.isValid) {
            displayError(layoutNameValidation.message!);
            return;
        }

        makeRequest<[string, string], void>(createLayoutAction)(
            layoutName,
            layout.origin
        )
            .then(() => {
                clearError();
                dispatchSwitchLayout(layout.origin, layoutName);
                setLayoutName('');
                forceLayoutListReload();
                onActionCompleted();
            })
            .catch((err) => {
                if (
                    isWellKnownLayoutApiErrorResponse(
                        err,
                        WellKnownLayoutApiErrorCodes.LayoutNewNameAlreadyExists
                    )
                ) {
                    displayError(
                        `A layout already exists with name '${layoutName}'. Please enter a new name.`
                    );
                } else {
                    const message = err.message || err.data.message;
                    if (message) {
                        dispatch(showWarningAsync(message));
                    }
                    displayError(`Unexpected error saving layout`);
                }
            });
    };

    return (
        <ControlledModal
            className="CreateLayoutModal"
            headerText="Create New Layout"
            saveText="Create Layout"
            isOpen={isOpen}
            onCancel={onActionCompleted}
            onSave={createLayout}
        >
            <div>
                <div className="title">Create New Layout</div>
                {errorMessage && <div className="error">{errorMessage}</div>}
                <TextInput
                    value={layoutName}
                    onChange={setInputTextAndClearInvalidState(setLayoutName)}
                    placeholder="Enter layout name"
                    error={invalidInput}
                    maxLength={100}
                    onEnter={createLayout}
                    autoFocus={true}
                />
            </div>
        </ControlledModal>
    );
}
